import type { ActionIcon } from "@afar-research/agent-content/actions";
import type { ThreadActionSelection } from "@afar-research/contracts";
import { ChevronDownIcon, XIcon } from "lucide-react";
import { memo } from "react";

import { cn } from "~/lib/utils";
import { Menu, MenuPopup, MenuRadioGroup, MenuRadioItem, MenuTrigger } from "../ui/menu";
import { actionIconComponent } from "./actionIcons";
import type { ComposerActionChipModel, ComposerActionEntry } from "./composerActions.logic";
import { composerFloatingLayerProps } from "./composerEventScope";

/**
 * The Action this message carries, at the top of the composer: its icon and
 * name, a dropdown for its variant when it has variants, and an × that takes it
 * off. An Action that cannot run here keeps its chip, dimmed, with the reason
 * beside it — the send refuses it rather than the chip vanishing.
 */
export const ComposerActionChip = memo(function ComposerActionChip(props: {
  chip: ComposerActionChipModel;
  onVariantChange: (variantId: string) => void;
  onRemove: () => void;
}) {
  const Icon = actionIconComponent(props.chip.icon);
  const variantLabel =
    props.chip.variants.find((variant) => variant.id === props.chip.variant)?.label ?? "Choose";
  return (
    <div
      className="flex min-w-0 items-center gap-2 px-3 pt-2.5 sm:px-4"
      data-composer-action-chip="true"
    >
      <span
        className={cn(
          "inline-flex h-7 shrink-0 items-center gap-1 rounded-lg border border-border/70 bg-muted/60 ps-2 pe-0.5 text-xs font-medium text-foreground",
          props.chip.disabledReason !== null && "opacity-64",
        )}
        title={props.chip.disabledReason ?? undefined}
      >
        <Icon aria-hidden="true" className="size-3.5 text-muted-foreground" />
        <span>{props.chip.label}</span>
        {props.chip.variants.length > 0 ? (
          <Menu>
            <MenuTrigger
              render={
                <button
                  type="button"
                  aria-label={`${props.chip.label} variant`}
                  className="inline-flex h-6 items-center gap-0.5 rounded-md px-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                />
              }
            >
              <span aria-hidden="true">·</span>
              {variantLabel}
              <ChevronDownIcon aria-hidden="true" className="size-3" />
            </MenuTrigger>
            <MenuPopup align="start" {...composerFloatingLayerProps}>
              <MenuRadioGroup
                value={props.chip.variant ?? ""}
                onValueChange={(value) => {
                  if (typeof value === "string" && value.length > 0) props.onVariantChange(value);
                }}
              >
                {props.chip.variants.map((variant) => (
                  <MenuRadioItem key={variant.id} value={variant.id}>
                    {variant.label}
                  </MenuRadioItem>
                ))}
              </MenuRadioGroup>
            </MenuPopup>
          </Menu>
        ) : null}
        <button
          type="button"
          aria-label={`Remove ${props.chip.label}`}
          className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          onClick={props.onRemove}
        >
          <XIcon aria-hidden="true" className="size-3" />
        </button>
      </span>
      {props.chip.disabledReason !== null ? (
        <span className="min-w-0 truncate text-muted-foreground text-xs">
          {props.chip.disabledReason}
        </span>
      ) : null}
    </div>
  );
});

/**
 * Replaces the input for an Action that takes no typed words (Lint, Delete
 * Brain sources, Delete design system). Deliberate, not broken: never a
 * greyed-out textarea. Removing the chip is the way back to typing.
 */
export const ComposerActionDescription = memo(function ComposerActionDescription(props: {
  icon: ActionIcon | null;
  description: string;
  destructive: boolean;
}) {
  const Icon = actionIconComponent(props.icon);
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-xl border px-3 py-3",
        props.destructive
          ? "border-rose-500/30 bg-rose-500/8 text-rose-800 dark:text-rose-200"
          : "border-border/70 bg-muted/40 text-muted-foreground",
      )}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <p className="min-w-0 text-[13px] leading-relaxed">{props.description}</p>
    </div>
  );
});

/**
 * The Next Action, one quiet row where the chip would sit: "Next · Run the
 * experiment". A suggestion, never a lock — clicking it only sets the chip.
 */
export const NextActionSuggestion = memo(function NextActionSuggestion(props: {
  entry: ComposerActionEntry;
  onPick: (selection: ThreadActionSelection) => void;
}) {
  const Icon = actionIconComponent(props.entry.icon);
  return (
    <div className="flex min-w-0 items-center px-3 pt-2.5 sm:px-4" data-composer-next-action="true">
      <button
        type="button"
        disabled={props.entry.disabledReason !== null}
        title={props.entry.disabledReason ?? props.entry.description}
        onClick={() => props.onPick(props.entry.selection)}
        className="inline-flex h-6 min-w-0 items-center gap-1.5 rounded-md px-1.5 text-muted-foreground text-xs hover:bg-accent hover:text-foreground disabled:opacity-64 disabled:hover:bg-transparent"
      >
        <span className="text-muted-foreground/70">Next</span>
        <span aria-hidden="true" className="text-muted-foreground/50">
          ·
        </span>
        <Icon aria-hidden="true" className="size-3.5 shrink-0" />
        <span className="truncate">{props.entry.label}</span>
      </button>
    </div>
  );
});
